import { useMutation, useQuery } from '@tanstack/react-query';
import { canInAnyScope } from '@testflow/shared';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { GoNoGo } from '../../components/reporting/go-no-go.js';
import { ClipTooltip } from '../../components/tooltip.js';
import { dashboardApi } from '../../lib/dashboard-api.js';
import { defectsApi } from '../../lib/defects-api.js';
import { defaultActivePlanId } from '../../lib/plan-selection.js';
import { plansApi } from '../../lib/plans-api.js';
// Whole-Branch-Review, Important 1: DIESELBE Sperr-Frage, die „Meine Tests" stellt — nicht eine
// zweite, hier nachgebaute (#83-Lehre: eine Quelle, mehrere Leser).
import { isLocked } from '../../lib/playlist-groups.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { reportsApi } from '../../lib/reports-api.js';
import { myDefectsApi, runsApi } from '../../lib/runs-api.js';
import { scopeHintFrom } from '../../lib/scope-hint.js';
import { isPendingSignoff, signoffApi } from '../../lib/signoff-api.js';
import { useCurrentProject } from '../../lib/use-project.js';

// #61: rollenadaptives Dashboard „Was ist für mich zu tun?". Ersetzt den leeren /dashboard-
// Platzhalter (app-shell.tsx). Jede Karte ist an genau ein Recht/Rolle gebunden (can()) und speist
// sich aus EINEM vorhandenen Endpunkt — kein neues Backend, keine KI (das KI-Briefing ist #62).
// Jede Karte trägt eine Konsequenz: einen Link auf ihre Vollseite (feature-durchstich — keine Karte
// ohne Wirkung). Plan-abhängige Karten (Sign-off, Go/No-Go) zeigen ohne aktiven Plan einen ruhigen
// Hinweis statt zu crashen. Der reine Tester landet gar nicht hier (app-shell isTesterOnly →
// MyTestsPage); diese Seite ist für die anderen/gemischten Rollen.

function DashboardCard({
  title,
  to,
  linkLabel,
  children,
}: {
  title: string;
  to: string;
  linkLabel: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3 rounded-card border border-[var(--border)] bg-bg-surface p-4">
      <h2 className="text-sm font-bold text-text-primary">{title}</h2>
      <div className="flex-1">{children}</div>
      <Link
        to={to}
        className="self-start rounded-btn border border-brand-accent px-3 py-1 text-xs text-brand-accent hover:bg-brand-accent/5"
      >
        {linkLabel} →
      </Link>
    </section>
  );
}

// Gemeinsamer Hinweis für alle plan-abhängigen Karten: solange die Plan-Liste lädt „lädt…", danach
// — wenn kein Plan aktiv ist — der ruhige Hinweis. Getrennt, damit der Hinweis nicht kurz aufblitzt,
// bevor die Pläne geladen sind.
function NoPlanNotice({ loading }: { loading: boolean }) {
  const { t } = useTranslation();
  return (
    <p className="text-sm text-text-muted">
      {loading ? t('common.loading') : t('dashboard.noActivePlan')}
    </p>
  );
}

// Karte 1 — Playlist (run.execute). Kurzinfo zu den offenen Läufen (dieselbe Quelle wie MyTests),
// Link ist Pflicht, die Zahl optional.
function PlaylistCard({
  projectId,
  planId,
  plansLoading,
}: {
  projectId: string;
  planId: string | null;
  plansLoading: boolean;
}) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['playlist', projectId, planId],
    queryFn: () => runsApi.playlist(projectId, planId ?? ''),
    enabled: Boolean(projectId && planId),
  });
  return (
    <DashboardCard
      title={t('dashboard.playlist.title')}
      to="/my-tests"
      linkLabel={t('dashboard.playlist.link')}
    >
      {planId ? (
        <p className="text-2xl font-bold text-text-primary">
          {data
            ? // #93: die Playlist liefert seitdem AUCH bestandene Testfälle (last_result), damit
              // MyTests die volle Kette anzeigen kann — „offene Läufe" muss die Erledigten also
              // selbst herausfiltern, sonst zählt die Kachel Bestandenes mit.
              // Whole-Branch-Review, Important 1: UND die gesperrten. „Meine Tests" ist seit Task
              // 10 der zweite Leser derselben Zeilen und trennt dort „7 offen · 5 wartend"
              // (`playlistCounts`). Ohne `isLocked` machte die Kachel die Trennung rückgängig: ein
              // Glied, dessen Vorgänger noch läuft, ist für den Tester nicht anfassbar (der Server
              // weist es mit 409 predecessor_not_done ab) — als „offener Lauf" auf dem Dashboard
              // behauptete es Arbeit und widersprach der Zahl daneben.
              t('dashboard.playlist.count', {
                count: data.filter(
                  (entry) => entry.last_result !== 'successful' && !isLocked(entry),
                ).length,
              })
            : t('common.loading')}
        </p>
      ) : (
        // Anti-Flacker wie bei Sign-off/Go-No-Go: solange die Pläne laden, „lädt…" statt sofort
        // „kein aktiver Plan" zu zeigen (planId ist waehrend des Ladens null).
        <NoPlanNotice loading={plansLoading} />
      )}
    </DashboardCard>
  );
}

// Karte 2 — Defect-Worklist (defect.edit). Zwei Reiter: „mir zugewiesen" (Board-Liste,
// client-gefiltert auf assignee_id === user) und „von mir gemeldet" (#60, /my-defects/my-reported).
// Beide Reiter tragen die Anzahl + eine Kurzliste + den Link zum Board.
function DefectWorklistCard({ projectId, userId }: { projectId: string; userId: string }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'assigned' | 'reported'>('assigned');

  // Query-Key wie das UNGEFILTERTE Board (['defects', projectId, {}]): teilt sich den Cache, wenn
  // das Board keinen aktiven Filter hat; mit Filter nutzt das Board einen anderen Key (dann ein
  // zweiter Fetch, harmlos).
  const { data: allDefects } = useQuery({
    queryKey: ['defects', projectId, {}],
    queryFn: () => defectsApi.list(projectId),
    enabled: Boolean(projectId),
  });
  const assigned = (allDefects ?? []).filter((d) => d.assignee_id === userId);

  const { data: reported } = useQuery({
    queryKey: ['my-reported', projectId],
    queryFn: () => myDefectsApi.myReported(projectId),
    enabled: Boolean(projectId),
  });

  // Beide Listen erfüllen dieselbe schmale Form (defect_id/human_id/title/status) → einheitliche
  // Zeilen-Darstellung, obwohl die Typen (DefectListEntry vs. MyDefect) verschieden sind.
  const rows = (tab === 'assigned' ? assigned : (reported ?? [])).slice(0, 5);
  const assignedCount = assigned.length;
  const reportedCount = reported?.length ?? 0;

  return (
    <DashboardCard
      title={t('dashboard.defects.title')}
      to="/defects"
      linkLabel={t('dashboard.defects.link')}
    >
      <div className="mb-2 flex overflow-hidden rounded-chip border border-[var(--border)] text-xs">
        {(['assigned', 'reported'] as const).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`px-2 py-1 ${
              tab === key ? 'bg-brand-accent text-white' : 'text-text-muted hover:text-text-body'
            }`}
          >
            {t(`dashboard.defects.${key}`)} ({key === 'assigned' ? assignedCount : reportedCount})
          </button>
        ))}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-text-muted">{t('dashboard.defects.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-1 text-sm">
          {rows.map((d) => (
            // #95: `truncate` stand vorher zusaetzlich auf der <li> selbst — dort ohne Wirkung
            // (Flex-Container, kein eigener Textinhalt), deshalb entfernt statt mitgeschleppt.
            <li key={d.defect_id} className="flex items-center gap-2">
              <span className="font-mono text-[11px] text-text-muted">{d.human_id}</span>
              {/* Kurzvorschau ohne Zeilen-Link (der Link fuehrt aufs ganze Board) — kein
                  fokussierbares Kind zum Umschliessen. tabIndex macht den Titel selbst zum
                  Fokus-Ziel, sonst waere die Blase nur mit der Maus erreichbar. */}
              <ClipTooltip text={d.title} className="min-w-0 flex-1">
                {/* biome-ignore lint/a11y/noNoninteractiveTabindex: reiner Lesetext ohne
                    Klickziel — der Tab-Stop existiert nur, damit die ClipTooltip-Blase (#95)
                    per Tastatur erreichbar ist. */}
                <span data-clip tabIndex={0} className="block truncate text-text-body">
                  {d.title}
                </span>
              </ClipTooltip>
            </li>
          ))}
        </ul>
      )}
    </DashboardCard>
  );
}

// Karte 3 — Sign-off (signoff.confirm). Ausstehende Abnahmen des Default-aktiven Plans plus die
// Anzahl nicht-zugeordneter In-Scope-Testfälle (#34).
function SignoffCard({
  projectId,
  planId,
  plansLoading,
}: {
  projectId: string;
  planId: string | null;
  plansLoading: boolean;
}) {
  const { t } = useTranslation();
  const { data: signoffs } = useQuery({
    queryKey: ['signoffs', projectId, planId],
    queryFn: () => signoffApi.list(projectId, planId ?? ''),
    enabled: Boolean(projectId && planId),
  });
  const { data: unassigned } = useQuery({
    queryKey: ['signoffs-unassigned', projectId, planId],
    queryFn: () => signoffApi.unassigned(projectId, planId ?? ''),
    enabled: Boolean(projectId && planId),
  });
  // Whole-Branch-Review: `signoffApi.list` liefert seit dem Umbau von #83 wieder ALLE Knoten
  // (auch die ohne Testfall im Plan-Scope, s. signoff-api.ts). Für "ausstehende Abnahmen" zählt ein
  // Knoten ohne Testfall im Scope NICHT mit — es gibt dort nichts abzunehmen, ein Zähler wäre nur
  // Rauschen (bewusst KEINE eigene Anzeige/Disclosure hier, anders als auf der Sign-off-Seite: die
  // Dashboard-Karte ist eine reine Zusammenfassung, kein Arbeitsbereich). Vorher übernahm der
  // Server diese Ausblendung implizit (GET /signoffs filterte sie serverseitig weg); jetzt macht
  // die Karte es explizit über isPendingSignoff, sonst würde die Zahl hier plötzlich zu hoch.
  const pending = (signoffs ?? []).filter(isPendingSignoff).length;

  return (
    <DashboardCard
      title={t('dashboard.signoff.title')}
      to="/signoff"
      linkLabel={t('dashboard.signoff.link')}
    >
      {!planId ? (
        <NoPlanNotice loading={plansLoading} />
      ) : signoffs === undefined ? (
        // Solange die Sign-off-Liste laedt, „lädt…" statt kurz „0 offene Abnahmen" zu zeigen
        // (konsistent mit der Go/No-Go-Karte).
        <p className="text-sm text-text-muted">{t('common.loading')}</p>
      ) : (
        <div className="flex flex-col gap-1">
          <p className="text-2xl font-bold text-text-primary">
            {t('dashboard.signoff.pending', { count: pending })}
          </p>
          {unassigned && unassigned.count > 0 ? (
            <p className="text-sm text-status-warn">
              {t('dashboard.signoff.unassigned', { count: unassigned.count })}
            </p>
          ) : null}
        </div>
      )}
    </DashboardCard>
  );
}

// #97: Vorzeichen-Format wie im Digest (formatDelta, apps/api/src/services/digest/render.ts) —
// von dort NICHT importierbar (API-Paket, nicht im Web-Bundle), deshalb hier lokal nachgebaut.
function formatDelta(n: number): string {
  if (n > 0) return `+${n}`;
  if (n < 0) return `${n}`;
  return '±0';
}

// Karte 4 — Go/No-Go (report.read). Lagebild des Default-aktiven Plans: Go/No-Go-Badge, Coverage-%,
// offene Blocker auf In-Scope-Prozessen.
function GoNoGoCard({
  projectId,
  planId,
  plansLoading,
}: {
  projectId: string;
  planId: string | null;
  plansLoading: boolean;
}) {
  const { t } = useTranslation();
  const { data: summary } = useQuery({
    queryKey: ['report-summary', projectId, planId],
    queryFn: () => reportsApi.summary(projectId, planId ?? ''),
    enabled: Boolean(projectId && planId),
  });

  return (
    <DashboardCard
      title={t('dashboard.status.title')}
      to="/reporting"
      linkLabel={t('dashboard.status.link')}
    >
      {!planId ? (
        <NoPlanNotice loading={plansLoading} />
      ) : summary ? (
        <div className="flex flex-col gap-2">
          <GoNoGo
            value={summary.exitCriteria.goNoGo}
            goLabel={t('dashboard.status.go')}
            noGoLabel={t('dashboard.status.noGo')}
            notConfiguredLabel={t('dashboard.status.notConfigured')}
          />
          {/* #86: ohne konfigurierte Exit-Kriterien ist Go/No-Go bedeutungslos — statt eines
              stillen neutralen Badges bekommt die Karte einen direkten Link zur Konfiguration. */}
          {summary.exitCriteria.goNoGo === 'not_configured' ? (
            <Link to="/admin/exit-criteria" className="text-sm text-brand-accent hover:underline">
              {t('dashboard.status.configureExit')}
            </Link>
          ) : null}
          <p className="text-sm text-text-body">
            {t('dashboard.status.coverage', { pct: summary.coverage.coveragePct })}
          </p>
          {summary.severity.blockerOnInScopeTotal > 0 ? (
            <p className="text-sm text-status-fail">
              {t('dashboard.status.blockers', { count: summary.severity.blockerOnInScopeTotal })}
            </p>
          ) : (
            <p className="text-sm text-text-muted">{t('dashboard.status.noBlockers')}</p>
          )}
          {/* #92: dieselbe Regel wie die Retest-Sektion der Playlist/die Reporting-KPI-Karte — nur
              sichtbar, wenn tatsächlich etwas wartet (kein "0 Testfälle"-Rauschen). */}
          {summary.retestDueCount > 0 ? (
            <p className="text-sm text-status-warn">
              {t('dashboard.status.retestDue', { count: summary.retestDueCount })}
            </p>
          ) : null}
          {/* #97: nur wenn sinceYesterday nicht null ist (kein Vortages-Snapshot vorhanden) —
              auch bei ±0-Werten rendern, sobald ein Vergleichswert existiert. */}
          {summary.sinceYesterday ? (
            <p className="text-sm text-text-muted">
              {t('dashboard.sinceYesterday', {
                passed: formatDelta(summary.sinceYesterday.passed),
                blocker: formatDelta(summary.sinceYesterday.openBlocker),
              })}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-text-muted">{t('common.loading')}</p>
      )}
    </DashboardCard>
  );
}

// Karte 5 — Admin (isAdmin || project.configure). Nur Shortcuts, keine Zahlen. Der Import-Link
// bleibt Admin-exklusiv (wie die admin/import-Route in app-shell.tsx).
function AdminCard({ isAdmin }: { isAdmin: boolean }) {
  const { t } = useTranslation();
  return (
    <section className="flex flex-col gap-3 rounded-card border border-[var(--border)] bg-bg-surface p-4">
      <h2 className="text-sm font-bold text-text-primary">{t('dashboard.admin.title')}</h2>
      <div className="flex flex-1 flex-col gap-2">
        <Link
          to="/admin"
          className="rounded-btn border border-[var(--border)] px-3 py-1.5 text-sm text-text-body hover:text-brand-accent"
        >
          {t('dashboard.admin.users')}
        </Link>
        {isAdmin ? (
          <Link
            to="/admin/import"
            className="rounded-btn border border-[var(--border)] px-3 py-1.5 text-sm text-text-body hover:text-brand-accent"
          >
            {t('dashboard.admin.import')}
          </Link>
        ) : null}
      </div>
    </section>
  );
}

// #62: Opt-in KI-Briefing OBEN auf dem Dashboard. Ein Klick sammelt die schon berechneten Aggregate
// der aktuellen Rolle und lässt ein LLM 2-3 Sätze Handlungsempfehlung formulieren (Muster wie der
// Prognose-Button in reporting-page.tsx: on-demand-Mutation, kein Auto-Call). Die Aggregate werden
// über DIESELBEN Query-Keys wie die Karten gelesen — react-query teilt den Cache, es entsteht kein
// Doppel-Fetch. Ohne Gateway kommt {text:null} zurück → ehrlicher Hinweis statt einer erfundenen
// Empfehlung (wie reporting.etaDegraded). Die Rolle leitet der Server aus dem Login ab, nicht hier.
function BriefingBar({
  projectId,
  planId,
  userId,
  canExecute,
  canDefects,
  canSignoff,
  canReport,
  scopeActive,
}: {
  projectId: string;
  planId: string | null;
  userId: string;
  canExecute: boolean;
  canDefects: boolean;
  canSignoff: boolean;
  canReport: boolean;
  // Review-Fund 2: true, sobald der Zuschnitt-Hinweis der Seite aktiv ist (scopeHintFrom !== null).
  // Ohne dieses Signal mischt der Prompt knoten-gescopte Zahlen (go_no_go/blockers/pending_signoffs/
  // unassigned) mit den weiterhin projektweiten (open_runs/assigned_defects/reported_defects) zu
  // einem Satz, als wären es dieselbe Grundmenge (role-briefing.md erklärt dem LLM die Aufteilung).
  scopeActive: boolean;
}) {
  const { t } = useTranslation();

  // Gleiche Query-Keys wie die Karten → geteilter Cache, kein zweiter Netz-Aufruf. Jede Query ist
  // an ihr Karten-Recht gebunden, damit eine Rolle nur ihre eigenen Zahlen sammelt.
  const { data: playlist } = useQuery({
    queryKey: ['playlist', projectId, planId],
    queryFn: () => runsApi.playlist(projectId, planId ?? ''),
    enabled: Boolean(projectId && planId && canExecute),
  });
  const { data: allDefects } = useQuery({
    queryKey: ['defects', projectId, {}],
    queryFn: () => defectsApi.list(projectId),
    enabled: Boolean(projectId && canDefects),
  });
  const { data: reported } = useQuery({
    queryKey: ['my-reported', projectId],
    queryFn: () => myDefectsApi.myReported(projectId),
    enabled: Boolean(projectId && canDefects),
  });
  const { data: signoffs } = useQuery({
    queryKey: ['signoffs', projectId, planId],
    queryFn: () => signoffApi.list(projectId, planId ?? ''),
    enabled: Boolean(projectId && planId && canSignoff),
  });
  const { data: unassigned } = useQuery({
    queryKey: ['signoffs-unassigned', projectId, planId],
    queryFn: () => signoffApi.unassigned(projectId, planId ?? ''),
    enabled: Boolean(projectId && planId && canSignoff),
  });
  const { data: summary } = useQuery({
    queryKey: ['report-summary', projectId, planId],
    queryFn: () => reportsApi.summary(projectId, planId ?? ''),
    enabled: Boolean(projectId && planId && canReport),
  });

  const briefingMutation = useMutation({
    // Nur die Zahlen zu Karten, die diese Rolle überhaupt sieht, werden geschickt (sonst undefined →
    // von JSON.stringify verworfen). Die Rolle ist bewusst NICHT dabei — der Server leitet sie ab.
    mutationFn: () =>
      dashboardApi.briefing(projectId, {
        // #93: gleiche Filterung wie in PlaylistCard — die Playlist enthält seitdem auch bestandene
        // Testfälle, „offene Läufe" zählt nur die noch nicht bestandenen.
        // Whole-Branch-Review, Important 1: samt `isLocked` (s. PlaylistCard oben). Sonst erzählt
        // das Briefing eine andere Geschichte als die Kachel darüber — aus derselben Datenzeile.
        open_runs: canExecute
          ? (playlist ?? []).filter(
              (entry) => entry.last_result !== 'successful' && !isLocked(entry),
            ).length
          : undefined,
        assigned_defects: canDefects
          ? (allDefects ?? []).filter((d) => d.assignee_id === userId).length
          : undefined,
        reported_defects: canDefects ? (reported?.length ?? 0) : undefined,
        // Dieselbe Regel wie in SignoffCard oben (isPendingSignoff): ein Knoten ohne Testfall im
        // Plan-Scope zählt nicht als ausstehende Abnahme, sonst weicht die KI-Briefing-Zahl von der
        // sichtbaren Kartenzahl ab.
        pending_signoffs: canSignoff ? (signoffs ?? []).filter(isPendingSignoff).length : undefined,
        unassigned: canSignoff ? (unassigned?.count ?? 0) : undefined,
        // #86: DashboardBriefingRequestSchema erlaubt bewusst nur 'go'/'no_go' (Prompt-Schutz vor
        // Freitext-Werten) — 'not_configured' ist dort kein sinnvoller Wert und wird wie "kein
        // Ergebnis" behandelt (undefined), statt den Zod-Typ aufzuweichen.
        go_no_go:
          canReport && summary?.exitCriteria.goNoGo !== 'not_configured'
            ? summary?.exitCriteria.goNoGo
            : undefined,
        blockers: canReport ? (summary?.severity.blockerOnInScopeTotal ?? 0) : undefined,
        // Review-Fund 2: Boolean statt Knotenname — kleinstmögliche Angriffsfläche fürs Prompt,
        // dieselbe Zurückhaltung wie beim `go_no_go`-Enum oben (kein Freitext ins LLM).
        scope_active: scopeActive,
      }),
  });

  return (
    <section className="flex flex-col gap-2 rounded-card border border-[var(--border)] bg-bg-surface p-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => briefingMutation.mutate()}
          disabled={briefingMutation.isPending}
          className="rounded-btn border border-brand-accent px-3 py-1 text-xs text-brand-accent hover:bg-brand-accent/5 disabled:opacity-50"
        >
          ✦ {t('dashboard.briefing.button')}
        </button>
        <span className="text-xs text-text-muted">{t('dashboard.briefing.hint')}</span>
      </div>
      {briefingMutation.data ? (
        briefingMutation.data.text ? (
          <div className="rounded-btn bg-[var(--tint-blue)] px-3 py-2 text-sm text-text-body">
            ✦ {briefingMutation.data.text}
          </div>
        ) : (
          <div className="text-xs text-text-muted">{t('dashboard.briefing.degraded')}</div>
        )
      ) : null}
    </section>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  // #23: Rollen DIESES Projekts (lib/project-roles.ts) — sonst zeigt das Dashboard Karten, deren
  // Recht der Nutzer nur in einem ANDEREN Projekt hat.
  const { user } = useScopedUser();
  const { project } = useCurrentProject();
  const projectId = project?.project_id ?? '';

  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ['plans', projectId],
    queryFn: () => plansApi.list(projectId),
    enabled: Boolean(projectId),
  });
  const activePlanId = defaultActivePlanId(plans ?? []);

  // Fund 2 (Whole-Branch-Review): Zuschnitt-Hinweis wie in reporting-page.tsx/signoff-page.tsx —
  // useScopedUser() liefert den bereits projekt-gescopten Nutzer (#23). Als Knotenliste dient,
  // sofern vorhanden, dieselbe Sign-off-Abfrage wie SignoffCard weiter unten: identischer
  // Query-Key + identische queryFn -> geteilter react-query-Cache, KEIN Zusatz-Request. Ohne
  // signoff.confirm-Recht (kein Verantwortlicher — die einzige scope-fähige Rolle, s. roles.ts
  // SCOPED_ROLES) bleibt die Liste leer, und scopeHintFrom (Fund 1) fällt auf den namenlosen
  // Rückfalltext zurück, statt eine neue Abfrage zu erfinden.
  const canSignoffForHint = user ? canInAnyScope(user, 'signoff.confirm') : false;
  const { data: signoffsForHint } = useQuery({
    queryKey: ['signoffs', projectId, activePlanId],
    queryFn: () => signoffApi.list(projectId, activePlanId ?? ''),
    enabled: Boolean(projectId && activePlanId && canSignoffForHint),
  });
  const nodesForHint = (signoffsForHint ?? []).map((it) => ({
    node_id: it.node_id,
    name: it.node_name,
  }));
  const scopeHint = scopeHintFrom(user, nodesForHint);

  if (!user) return <div className="p-6 text-text-muted">{t('common.loading')}</div>;

  const isAdmin = user.roles.some((r) => r.role === 'admin');
  const canExecute = canInAnyScope(user, 'run.execute');
  const canDefects = canInAnyScope(user, 'defect.edit');
  const canSignoff = canInAnyScope(user, 'signoff.confirm');
  const canReport = canInAnyScope(user, 'report.read');
  const canConfigure = isAdmin || canInAnyScope(user, 'project.configure');

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-lg font-bold text-text-primary">{t('dashboard.title')}</h1>
        <p className="text-sm text-text-muted">{t('dashboard.subtitle')}</p>
      </div>
      {/* Fund 2 (Review): ohne diese Zeile zeigt die Statuskarte unten (Projektstatus/Coverage/
          Blocker) und die Abnahmekarte fuer einen zugeschnittenen Verantwortlichen unbeschriftet
          beschnittene Zahlen — direkt neben der Defect-Karte, die (bewusst, Stufe 1) projektweit
          bleibt. Eigener Textschluessel statt des allgemeinen `scope.hint`: der spricht von "allen
          Zahlen auf dieser Seite", was hier neben der projektweiten Defect-Karte falsch waere. */}
      {scopeHint ? (
        <p className="text-sm text-text-muted" data-testid="scope-hint" aria-live="polite">
          {scopeHint.label !== null
            ? t('scope.hintDashboard', { nodes: scopeHint.label })
            : t('scope.hintDashboardFallback')}
        </p>
      ) : null}
      {/* Das Briefing hängt am report.read-Endpunkt — nur zeigen, wer das Recht hat (in V1 alle
          Dashboard-Rollen, aber konsistent mit dem Endpunkt-Gate und robust gegen gescopte Rollen). */}
      {canReport ? (
        <BriefingBar
          projectId={projectId}
          planId={activePlanId}
          userId={user.user_id}
          canExecute={canExecute}
          canDefects={canDefects}
          canSignoff={canSignoff}
          canReport={canReport}
          scopeActive={scopeHint !== null}
        />
      ) : null}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {canExecute ? (
          <PlaylistCard projectId={projectId} planId={activePlanId} plansLoading={plansLoading} />
        ) : null}
        {canDefects ? <DefectWorklistCard projectId={projectId} userId={user.user_id} /> : null}
        {canSignoff ? (
          <SignoffCard projectId={projectId} planId={activePlanId} plansLoading={plansLoading} />
        ) : null}
        {canReport ? (
          <GoNoGoCard projectId={projectId} planId={activePlanId} plansLoading={plansLoading} />
        ) : null}
        {canConfigure ? <AdminCard isAdmin={isAdmin} /> : null}
      </div>
    </div>
  );
}
